import { useEffect, useMemo, useCallback } from 'react';
import { useToast } from './../../../hooks/useToast';
import { FlattenedUser } from './../../../types/user/index';
import { gql } from '@apollo/client';
import { useSelector } from 'react-redux';
import Globals from '../../../lib/Globals';
import {
	UseDocumentSettingsModalShareListCustomDocumentWithDownloadUrlFragment,
	UseDocumentSettingsModalShareListContactFragment,
	DocumentType,
	DocumentOwnerType,
	UserType,
	useDocumentSettingsModalShareList_GetOrganizationsForUserQuery,
} from '#types';
import rpcShared from '@rockpapercoin/rpc-shared';
import ContactCustomerAvatar from '../../ContactCustomerAvatar';

export const useDocumentSettingsModalShareListContactFragment = gql`
	fragment useDocumentSettingsModalShareListContact on Contact {
		id
		firstName
		lastName
		customer {
			id
			email
		}
		vendor {
			id
			image
			updatedAt
			name
		}
		...contactCustomerAvatarContact
	}
	${ ContactCustomerAvatar.fragments.contactCustomerAvatarContactFragment }
`;

export const useDocumentSettingsModalShareListCustomDocumentWithDownloadUrlFragment = gql`
	fragment useDocumentSettingsModalShareListCustomDocumentWithDownloadUrl on Document {
		id
		type
		ownerType
		customer {
			id
			email
			clientUser {
				id
				updatedAt
				firstNameOne
				lastNameOne
				plannerOrgHasDocumentsPermissions
				assignedPlanner {
					id
					organization {
						id
					}
				}
			}
			orgUser {
				id
				updatedAt
				firstName
				lastName
			}
		}
		organization {
			id
			name
		}
		sharedWithContacts {
			...useDocumentSettingsModalShareListContact
		}
		sharedWithCustomer
	}
	${ useDocumentSettingsModalShareListContactFragment }
`;

export const useDocumentSettingsModalShareListOrganizationFragment = gql`
	fragment useDocumentSettingsModalShareListOrganization on Organization {
		id
		contacts(where: $contactWhere) {
			...useDocumentSettingsModalShareListContact
		}
	}
	${ useDocumentSettingsModalShareListContactFragment }
`;

export type UseDocumentSettingsModalShareListProps = {
	useDocumentSettingsModalShareList_GetOrganizationsForUserQuery: typeof useDocumentSettingsModalShareList_GetOrganizationsForUserQuery;
	customerId?: string;
	document: UseDocumentSettingsModalShareListCustomDocumentWithDownloadUrlFragment;
	setSharedWithContacts: React.Dispatch<
	React.SetStateAction<UseDocumentSettingsModalShareListContactFragment[]>
	>;
	sharedWithContacts: UseDocumentSettingsModalShareListContactFragment[];
	setSharedWithCustomer: React.Dispatch<React.SetStateAction<boolean>>;
	sharedWithCustomer: boolean;
	isOwner: boolean;
};

export const useDocumentSettingsModalShareList = ( {
	useDocumentSettingsModalShareList_GetOrganizationsForUserQuery,
	customerId,
	document,
	setSharedWithContacts,
	sharedWithContacts,
	setSharedWithCustomer,
	sharedWithCustomer,
	isOwner,
}: UseDocumentSettingsModalShareListProps ) => {
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );
	const { showError } = useToast();
	const nonCustomerOrgUser =
		user?.isLoggedIn &&
		user.userType === UserType.OrgUser &&
		user.user.id !== customerId
			? user
			: undefined;
	const {
		data: organizationsData,
		error: organizationsError,
		loading,
	} = useDocumentSettingsModalShareList_GetOrganizationsForUserQuery( {
		variables: {
			where: { id: customerId || '' },
			take: Globals.itemReturnLimit,
		},
		skip: !customerId || !user?.isLoggedIn,
	} );

	useEffect( () => {
		if ( organizationsError ) {
			showError( organizationsError );
		}
	}, [ organizationsError, showError ] );

	const organizations =
		organizationsData?.getOrganizationsForUser?.organizations;

	const organizationContacts = useMemo(
		() =>
			organizations
				? organizations.reduce(
					( acc, organization ) =>
						organization.contacts && customerId
							? [
								...acc,
								...organization.contacts.filter(
									( contact ) => contact.customer.id === customerId
								),
								  ]
							: acc,
					[] as UseDocumentSettingsModalShareListContactFragment[]
				  )
				: [],
		[ customerId, organizations ]
	);

	const contactBetweenCustomerAndActingVendor = useMemo(
		() =>
			organizationContacts.find(
				( c ) =>
					c.customer.id === customerId &&
					c.vendor.id === nonCustomerOrgUser?.organization.id
			),
		[
			organizationContacts,
			customerId,
			nonCustomerOrgUser
		]
	);

	useEffect( () => {
		setSharedWithContacts( document.sharedWithContacts || [] );
		setSharedWithCustomer( document.sharedWithCustomer || false );
	}, [
		document,
		setSharedWithContacts,
		setSharedWithCustomer
	] );

	const toggleShareWithOrganization = useCallback(
		( organizationContact: UseDocumentSettingsModalShareListContactFragment ) => {
			setSharedWithContacts( ( prevState ) => {
				const item = prevState.find(
					( contact ) => contact.id === organizationContact.id
				);
				if ( item ) {
					return prevState.filter(
						( contact ) => contact.id !== organizationContact.id
					);
				} else {
					return [ ...prevState, organizationContact ];
				}
			} );
		},
		[ setSharedWithContacts ]
	);

	const getUsername = useCallback(
		(
			customer: UseDocumentSettingsModalShareListCustomDocumentWithDownloadUrlFragment['customer']
		) =>
			nonCustomerOrgUser && contactBetweenCustomerAndActingVendor
				? rpcShared.contacts.getCustomerName(
					contactBetweenCustomerAndActingVendor
				  ).fullName
				: customer?.clientUser
					? `${ customer?.clientUser?.firstNameOne } ${ customer?.clientUser?.lastNameOne }`
					: customer?.orgUser
						? `${ customer?.orgUser?.firstName } ${ customer?.orgUser?.lastName }`
						: '',
		[ nonCustomerOrgUser, contactBetweenCustomerAndActingVendor ]
	);

	const organizationsToDisplay = organizationContacts.reduce(
		(
			acc,
			organizationContact: UseDocumentSettingsModalShareListContactFragment
		) => {
			// Do not display if org is owner
			if (
				document &&
				document.ownerType === DocumentOwnerType.Organization &&
				nonCustomerOrgUser &&
				nonCustomerOrgUser.organization.id === organizationContact.vendor.id
			)
				return acc;

			// Do not display if customer is owner and the user is not the customer
			if (
				document &&
				document.ownerType === DocumentOwnerType.Customer &&
				!isOwner
			)
				return acc;

			// otherwise it's legit to display
			return [ ...acc, organizationContact ];
		},
		[] as UseDocumentSettingsModalShareListContactFragment[]
	);

	const documentIsShared =
		document.ownerType === DocumentOwnerType.Organization
			? !isOwner
				? sharedWithContacts.length > 0 ||
				  ( nonCustomerOrgUser ? sharedWithCustomer : false )
				: organizationsToDisplay.length > 0
			: !isOwner
				? sharedWithContacts.filter(
					( contact ) => contact.vendor.id !== nonCustomerOrgUser?.organization.id
			  ).length > 0
				: sharedWithContacts.length > 0;

	return {
		visible: document.type === DocumentType.Sharable && isOwner,
		organizationsToDisplay,
		documentIsShared,
		toggleShareWithOrganization,
		getUsername,
		loading,
		organizations,
		organizationContacts,
		contactBetweenCustomerAndActingVendor,
		nonCustomerOrgUser,
		isOwner,
		sharedWithContacts,
		sharedWithCustomer,
		setSharedWithContacts,
		setSharedWithCustomer,
	};
};
